/* eslint-disable max-classes-per-file */
import React from 'react'
import Select from 'react-select'
import { FormItem, FormItemProps } from './FormItem'
import { IField, getRequiredError } from './FieldValidation'
import { Option } from './Options'
import { ErrorText } from './ErrorText'

export interface SelectFieldProps<V> extends FormItemProps {
  id: string
  disabled?: boolean
  onChange: { (value: V): any },
  value: V,
  dataSource: Option<V>[]
  required?: boolean
  requiredMessage?: string
  block?: boolean
  position?: string
}

export interface SelectFieldState {
  errorText?: string
}

export const selectStyles = (block: boolean, error: boolean, position?: string) => ({
  container: (provided: any, state: any) => ({
    ...provided,
    maxWidth: block === false ? '12.8125rem' : '',
  }),
  control: (provided: any, state: any) => ({
    ...provided,
    border: `3px solid ${error ? '#d60000' : 'gray'}`,
    backgroundColor: `${error && !state.isFocused ? '#fdf2f2' : '#ffffff'}`,
    boxShadow: 'none',
    padding: '3px',
    '&:hover': {
      border: `3px solid ${error ? '#d60000' : 'gray'}`,
    },
    outline: state.isFocused ? '3px solid #9263DE' : '',
    outlineOffset: state.isFocused ? '2px' : '',
  }),
  menu: (provided: any, state: any) => ({
    ...provided,
    zIndex: 200,
    ...(position && { position }),
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: '#ffffff',
    color: '#000000',
    '&:hover': {
      backgroundColor: '#c47ccf',
    },
    '&:active': {
      backgroundColor: '#893c94',
    },
  }),
})

export class SelectField<V> extends React.Component<SelectFieldProps<V>, SelectFieldState> implements IField {
  input: React.RefObject<any> = React.createRef()

  constructor(props?: SelectFieldProps<V>, context?: any) {
    super(props, context)

    this.state = {
    }
  }

  componentDidUpdate(prevProps: SelectFieldProps<V>, prevState: SelectFieldState) {
    if (this.props.value != prevProps.value) {
      if (this.state.errorText) {
        this.validate(() => { })
      }
    }
  }

  render() {
    let value: Option<V> = null
    if (this.props.dataSource) {
      for (const data of this.props.dataSource) {
        if (data.value == this.props.value) {
          value = data
          break
        }
      }
    }

    if (this.props.preview) {
      return (
        <FormItem {...this.props} inputId={this.props.id} errorMessage={this.state.errorText}>
          <div className="form-value">{value && value.label}</div>
        </FormItem>
      )
    }

    return (
      <FormItem {...this.props} inputId={this.props.id}>
        <Select
          styles={selectStyles(this.props.block, !!this.state.errorText, this.props.position)}
          id={this.props.id}
          ref={this.input}
          options={this.props.dataSource}
          value={value}
          isClearable
          onChange={(v: Option<V>) => this.props.onChange(v === null ? null : v.value)}
          isDisabled={this.props.disabled}
        />
        <ErrorText text={this.state.errorText} />
      </FormItem>
    )
  }

  focus() {
    if (this.input.current) {
      const selectElement = this.input.current as any
      if (selectElement && selectElement.focus) {
        selectElement.focus()
      }
    }
  }

  hasValue(): boolean {
    let res = false
    if (this.props.dataSource) {
      for (const data of this.props.dataSource) {
        if (data.value == this.props.value) {
          res = true
          break
        }
      }
    }
    return res
  }

  getErrorText(): string {
    if (this.props.required === true) {
      if (!this.hasValue()) {
        return getRequiredError(this.props)
      }
    }
    return null
  }

  validate(callback: { (res: boolean): any }, focus?: boolean) {
    const errorText = this.getErrorText()
    this.setError(errorText, () => {
      if (this.props.errorMessage || errorText) {
        if (focus === true) {
          this.focus()
        }
      }

      callback && callback(!((this.props.errorMessage || errorText)))
    })
  }

  setError(errorText?: string, callback?: { (): any }) {
    this.setState({ errorText }, callback)
  }
}

export class TextSelectField extends SelectField<string> { }

export class NumericSelectField extends SelectField<number> { }

export class BooleanSelectField extends SelectField<boolean> { }
